import * as THREE from "three";
import { Renderer } from "../core/renderer.js";
import { SceneManager } from "../core/scene_manager.js";
import { canvasHasContext } from "../ui/dom_backend/dom_backend.js";
import { ImagesHolder } from "../image_loader/images_holder.js";
import { audioController } from "../audio/audio_controller.js";
import { InstanceBuffer } from "../webgl_2d/core/rendering/instance_buffer.js";
import { UtilityPlugin, NativeMemoryInfo } from "../plugins/utility_plugin.js";
import { runWhenIdle } from "../utils/idle_task.js";

export interface MemoryEstimate
{
  js_heap: number;
  js_heap_limit: number;
  canvases_n: number;
  canvases_bytes: number;
  gl_framebuffer_bytes: number;
  three_geometries_n: number;
  three_geometry_bytes: number;
  three_textures_n: number;
  three_texture_bytes: number;
  three_shadow_bytes: number;
  three_info_geometries: number;
  three_info_textures: number;
  three_info_programs: number;
  images_n: number;
  images_bytes: number;
  instance_buffers_bytes: number;
  audio_n: number;
  audio_bytes: number;
  total_bytes: number;
}

export interface MemorySample
{
  at: number;
  estimate: MemoryEstimate;
  native: NativeMemoryInfo | null;
}

export type MemoryHistoryRow = [ number, number, number, number, number, number, number, number, number ];

const HISTORY_KEY = "bb_mem_history_v1";
const HISTORY_MAX = 150;
const SAMPLE_INTERVAL_MS = 2000;
const PERSIST_EVERY_SAMPLES = 5;
const NATIVE_EVERY_SAMPLES = 5;

export class MemoryProbe
{
  private static timer: ReturnType<typeof setInterval> | null = null;
  private static last_sample: MemorySample | null = null;
  private static native_cache: NativeMemoryInfo | null = null;
  private static native_pending = false;
  private static samples_since_native = NATIVE_EVERY_SAMPLES;
  private static native_supported = true;
  private static rows: MemoryHistoryRow[] = [];
  private static previous_rows: MemoryHistoryRow[] = [];
  private static samples_since_persist = 0;
  private static listeners = new Set<( sample: MemorySample ) => void>();

  public static start(): void
  {
    if ( this.timer !== null )
      return;
    this.previous_rows = this.readPersisted();
    try { localStorage.removeItem( HISTORY_KEY ); }
    catch {}
    this.timer = setInterval( () => this.tick(), SAMPLE_INTERVAL_MS );
    this.tick();
  }

  public static last(): MemorySample | null
  {
    return this.last_sample;
  }

  public static history(): MemoryHistoryRow[]
  {
    return this.rows;
  }

  public static previousRun(): MemoryHistoryRow[]
  {
    return this.previous_rows;
  }

  public static subscribe( fn: ( sample: MemorySample ) => void ): () => void
  {
    this.listeners.add( fn );
    return () => { this.listeners.delete( fn ); };
  }

  public static sampleNow(): MemorySample
  {
    this.tick();
    return this.last_sample!;
  }

  public static forSnapshot(): Record<string, number> | null
  {
    const s = this.last_sample;
    if ( !s )
      return null;
    const mb = ( v: number | undefined ) => ( v === undefined || v < 0 ? -1 : Math.round( v / 1048576 ) );
    const out: Record<string, number> = {
      est_mb: mb( s.estimate.total_bytes ),
      canvas_mb: mb( s.estimate.canvases_bytes + s.estimate.gl_framebuffer_bytes ),
      three_mb: mb( s.estimate.three_geometry_bytes + s.estimate.three_texture_bytes + s.estimate.three_shadow_bytes ),
      images_mb: mb( s.estimate.images_bytes ),
      vfx_mb: mb( s.estimate.instance_buffers_bytes ),
    };
    if ( s.estimate.js_heap >= 0 )
      out.js_heap_mb = mb( s.estimate.js_heap );
    if ( s.native )
    {
      out.app_mb = mb( s.native.app_footprint );
      out.renderer_mb = mb( s.native.renderer_footprint );
      out.device_avail_mb = mb( s.native.device_available );
      out.app_limit_mb = mb( s.native.app_limit );
    }
    return out;
  }

  public static estimate(): MemoryEstimate
  {
    const perf_mem = ( performance as { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } } ).memory;
    const js_heap = perf_mem?.usedJSHeapSize ?? -1;
    const js_heap_limit = perf_mem?.jsHeapSizeLimit ?? -1;

    let canvases_n = 0;
    let canvases_bytes = 0;
    let gl_framebuffer_bytes = 0;
    try
    {
      const gl_canvas = Renderer.renderer?.domElement ?? null;
      const released = Renderer.isBufferReleased();
      const list = Array.from( document.querySelectorAll( "canvas" ) ) as HTMLCanvasElement[];
      for ( const c of list )
      {
        const is_gl = c === gl_canvas
          || c.classList.contains( "webgl-canvas" )
          || c.classList.contains( "webgl-canvas-overlay" );
        if ( !is_gl && !canvasHasContext( c ) )
          continue;
        canvases_n++;
        if ( c === gl_canvas && released )
          continue;
        const bytes = c.width * c.height * 4;
        canvases_bytes += bytes;
        if ( c === gl_canvas )
        {
          const msaa = localStorage.getItem( "perf_diag_no_msaa" ) === "1" ? 1 : 4;
          const depth_bytes = bytes * msaa;
          gl_framebuffer_bytes += bytes * msaa + bytes + depth_bytes;
        }
      }
    }
    catch {}

    const three = this.measureThree();
    const images = this.safe( () => ImagesHolder.getMemoryStats(), { n: 0, bytes: 0, dom_n: 0, dom_bytes: 0 } );
    const audio = this.safe( () => audioController.getMemoryStats(), { n: 0, bytes: 0 } );
    const instance_buffers_bytes = this.safe( () => InstanceBuffer.getAllocatedBytes(), 0 );

    const total_bytes =
      Math.max( 0, js_heap ) +
      canvases_bytes + gl_framebuffer_bytes +
      three.geometry_bytes + three.texture_bytes + three.shadow_bytes +
      images.bytes + instance_buffers_bytes + audio.bytes;

    return {
      js_heap,
      js_heap_limit,
      canvases_n,
      canvases_bytes,
      gl_framebuffer_bytes,
      three_geometries_n: three.geometries_n,
      three_geometry_bytes: three.geometry_bytes,
      three_textures_n: three.textures_n,
      three_texture_bytes: three.texture_bytes,
      three_shadow_bytes: three.shadow_bytes,
      three_info_geometries: three.info_geometries,
      three_info_textures: three.info_textures,
      three_info_programs: three.info_programs,
      images_n: images.n,
      images_bytes: images.bytes,
      instance_buffers_bytes,
      audio_n: audio.n,
      audio_bytes: audio.bytes,
      total_bytes,
    };
  }

  private static tick_pending = false;

  private static tick(): void
  {
    if ( this.tick_pending ) return;
    this.tick_pending = true;
    runWhenIdle( () => { this.tick_pending = false; this.sample(); }, SAMPLE_INTERVAL_MS );
  }

  private static sample(): void
  {
    const estimate = this.estimate();
    const sample: MemorySample = { at: Date.now(), estimate, native: this.native_cache };
    this.last_sample = sample;
    this.pushRow( sample );
    for ( const fn of this.listeners )
    {
      try { fn( sample ); }
      catch {}
    }
    if ( ++this.samples_since_native >= NATIVE_EVERY_SAMPLES )
    {
      this.samples_since_native = 0;
      this.requestNative();
    }
  }

  private static requestNative(): void
  {
    if ( !this.native_supported || this.native_pending )
      return;
    this.native_pending = true;
    void UtilityPlugin.getMemoryInfo().then( info =>
    {
      this.native_pending = false;
      if ( !info )
      {
        this.native_supported = false;
        return;
      }
      this.native_cache = info;
      if ( this.last_sample )
        this.last_sample.native = info;
    } ).catch( () => { this.native_pending = false; } );
  }

  private static pushRow( s: MemorySample ): void
  {
    const mb = ( v: number | undefined ) => ( v === undefined || v < 0 ? -1 : Math.round( v / 1048576 ) );
    const e = s.estimate;
    const row: MemoryHistoryRow = [
      s.at,
      mb( e.total_bytes ),
      mb( e.canvases_bytes + e.gl_framebuffer_bytes ),
      mb( e.three_geometry_bytes + e.three_texture_bytes + e.three_shadow_bytes ),
      mb( e.images_bytes ),
      mb( e.js_heap ),
      mb( s.native?.app_footprint ),
      mb( s.native?.renderer_footprint ),
      mb( s.native?.device_available ),
    ];
    this.rows.push( row );
    if ( this.rows.length > HISTORY_MAX )
      this.rows.splice( 0, this.rows.length - HISTORY_MAX );
    if ( ++this.samples_since_persist >= PERSIST_EVERY_SAMPLES )
    {
      this.samples_since_persist = 0;
      try { localStorage.setItem( HISTORY_KEY, JSON.stringify( this.rows ) ); }
      catch {}
    }
  }

  private static readPersisted(): MemoryHistoryRow[]
  {
    try
    {
      const raw = localStorage.getItem( HISTORY_KEY );
      const parsed = raw ? JSON.parse( raw ) : null;
      return Array.isArray( parsed ) ? parsed as MemoryHistoryRow[] : [];
    }
    catch
    {
      return [];
    }
  }

  private static measureThree(): {
    geometries_n: number; geometry_bytes: number;
    textures_n: number; texture_bytes: number; shadow_bytes: number;
    info_geometries: number; info_textures: number; info_programs: number;
  }
  {
    const out = { geometries_n: 0, geometry_bytes: 0, textures_n: 0, texture_bytes: 0, shadow_bytes: 0, info_geometries: 0, info_textures: 0, info_programs: 0 };
    try
    {
      const renderer = Renderer.renderer;
      if ( renderer )
      {
        out.info_geometries = renderer.info.memory.geometries;
        out.info_textures = renderer.info.memory.textures;
        out.info_programs = renderer.info.programs?.length ?? 0;
      }
      const scene = SceneManager.scene;
      if ( !scene )
        return out;

      const geometries = new Set<string>();
      const textures = new Set<string>();
      scene.traverse( obj =>
      {
        const mesh = obj as THREE.Mesh;
        const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
        if ( geometry && !geometries.has( geometry.uuid ) )
        {
          geometries.add( geometry.uuid );
          out.geometry_bytes += this.geometryBytes( geometry );
        }
        const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if ( material )
        {
          const list = Array.isArray( material ) ? material : [ material ];
          for ( const m of list )
          {
            for ( const value of Object.values( m as unknown as Record<string, unknown> ) )
            {
              const tex = value as THREE.Texture;
              if ( tex && ( tex as THREE.Texture ).isTexture && !textures.has( tex.uuid ) )
              {
                textures.add( tex.uuid );
                out.texture_bytes += this.textureBytes( tex );
              }
            }
          }
        }
        const light = obj as unknown as { shadow?: { map?: THREE.WebGLRenderTarget | null } };
        const shadow_map = light.shadow?.map;
        if ( shadow_map )
          out.shadow_bytes += shadow_map.width * shadow_map.height * 8;
      } );
      out.geometries_n = geometries.size;
      out.textures_n = textures.size;
    }
    catch {}
    return out;
  }

  private static geometryBytes( geometry: THREE.BufferGeometry ): number
  {
    let bytes = 0;
    for ( const attr of Object.values( geometry.attributes ) )
    {
      const array = ( attr as THREE.BufferAttribute ).array as ArrayBufferView | undefined;
      if ( array )
        bytes += array.byteLength;
    }
    if ( geometry.index?.array )
      bytes += ( geometry.index.array as ArrayBufferView ).byteLength;
    return bytes;
  }

  private static textureBytes( tex: THREE.Texture ): number
  {
    const image = tex.image as { width?: number; height?: number } | null;
    if ( !image || !image.width || !image.height )
      return 0;
    const base = image.width * image.height * 4;
    return tex.generateMipmaps ? Math.round( base * 4 / 3 ) : base;
  }

  private static safe<T>( fn: () => T, fallback: T ): T
  {
    try { return fn(); }
    catch { return fallback; }
  }
}
